// frontend/src/components/dialogs/ResetNotificationPreferencesDialog.js
import React from 'react';
import { RotateCcw, X, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmDialogShell from './ConfirmDialogShell';

/**
 * Specialized dialog for resetting notification preferences to defaults
 * Uses the standardized ConfirmDialogShell with enhanced warning messaging
 */
const ResetNotificationPreferencesDialog = ({
    isOpen,
    onClose,
    onConfirm,
    isResetting = false,
    entityName = 'organization'
}) => {
    const { t } = useTranslation();
    const { text, semantic, cn } = useTheme();

    return (
        <ConfirmDialogShell
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={onConfirm}
            title={t('resetNotificationPreferencesTitle')}
            confirmLabel={t('resetToDefaults')}
            cancelLabel={t('Cancel')}
            confirmVariant="warning"
            confirmIcon={RotateCcw}
            cancelIcon={X}
            isLoading={isResetting}
            showIcon={true}
            iconVariant="warning"
            size="md"
        >
            <div className="space-y-4 text-center">
                {/* Primary warning message */}
                <div className={cn('p-4 rounded-lg border', semantic.warning)}>
                    <p className={cn('font-medium', text.primary)}>
                        ⚠️ {t('resetNotificationPreferencesWarning')}
                    </p>
                </div>

                {/* Consequence explanation */}
                <p className={cn('text-sm', text.secondary)}>
                    {t('resetNotificationPreferencesConsequence', { entityName })}
                </p>

                {/* What will be reset */}
                <div className={cn('text-xs p-3 rounded border-l-4 border-amber-400', semantic.warning)}>
                    <p className={cn('font-medium mb-1', text.primary)}>
                        {t('What will be reset')}:
                    </p>
                    <ul className={cn('text-left space-y-1', text.secondary)}>
                        <li>• {t('All notification channel preferences')} (Email, SMS, Push)</li>
                        <li>• {t('All event type preferences')} (Booking, Cancellation, etc.)</li>
                        <li>• {t('Quiet hours settings')}</li>
                        <li>• {t('Any custom notification settings')}</li>
                    </ul>
                </div>

                {/* Impact warning */}
                <div className={cn('text-xs p-3 rounded border-l-4 border-red-400', 'bg-red-50 dark:bg-red-900/20')}>
                    <p className={cn('font-medium text-red-700 dark:text-red-400')}>
                        🚫 {t('This action cannot be undone')}
                    </p>
                </div>
            </div>
        </ConfirmDialogShell>
    );
};

export default ResetNotificationPreferencesDialog;